import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle2, Loader2Icon } from 'lucide-react'

import { useClinicWorkflow } from './hooks/use-clinic-workflow'
import StageRenderer from './components/stage-renderer'
import ValidationMessage from './components/validation-message'
import { useVibe, VibeProvider } from './context/vibe-context'
import type { AnswerMap, AnswerValue, WorkflowStage } from './types/workflow'
import './styles/weightwise-theme.css'

interface Glp1QuizPageProps {
  initialAnswers?: Record<string, unknown>
  initialStageId?: string
  initialCompletedAtByStage?: Record<string, string>
  progressStorageKey?: string
  onSubmit: (payload: { stages: unknown[] }) => Promise<void>
}

const PHASE_LABELS: Record<number, string> = {
  1: 'GETTING TO KNOW YOU',
  2: 'YOUR GOALS',
  3: 'BUILDING YOUR PLAN',
  4: 'KEEPING YOU SAFE',
  5: 'ALL DONE',
}

const NIMY_AVATAR_SRC = '/images/nimy-avatar.png'
const FALLBACK_AVATAR_SRC = '/img/jennie.webp'

function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  )
}

function NimyAvatar({ className = '' }: { className?: string }) {
  return (
    <div className={`shrink-0 w-8 h-8 rounded-full overflow-hidden border-[1.5px] border-[var(--ww-glass-border,rgba(255,255,255,0.08))] shadow-[0_0_12px_rgba(74,138,154,0.1)] ${className}`}>
      <img
        src={NIMY_AVATAR_SRC}
        alt="Nimy — your AI health guide"
        className="h-full w-full object-cover block"
        onError={(event) => {
          const target = event.currentTarget
          if (target.src.includes(FALLBACK_AVATAR_SRC)) return
          target.src = FALLBACK_AVATAR_SRC
        }}
      />
    </div>
  )
}

function TypingText({ text, onComplete }: { text: string; onComplete?: () => void }) {
  const [len, setLen] = useState(0)
  const done = len >= text.length

  useEffect(() => {
    setLen(0)
    let i = 0
    const id = setInterval(() => {
      i++
      if (i >= text.length) {
        setLen(text.length)
        clearInterval(id)
      } else {
        setLen(i)
      }
    }, 20)
    return () => clearInterval(id)
  }, [text])

  useEffect(() => {
    if (done) onComplete?.()
  }, [done, onComplete])

  return (
    <>
      {text.slice(0, len)}
      {!done && <span className="inline-block w-0.5 h-[0.8em] bg-[var(--ww-accent)] ml-px align-text-bottom animate-blink" />}
    </>
  )
}

function getStageHeading(stage: WorkflowStage | null): string {
  if (!stage) return ''
  const headerVar = stage.templateVariables.find((v) => v.title === 'header')
  if (typeof headerVar?.value === 'string' && headerVar.value.trim()) return headerVar.value
  return getCollapsedHeading(stage)
}

function getStageSubtitle(stage: WorkflowStage | null): string {
  if (!stage) return ''
  const subVar = stage.templateVariables.find((v) => v.title === 'subHeader' || v.title === 'sub_header')
  if (typeof subVar?.value === 'string' && subVar.value.trim()) return subVar.value
  if (stage.stageTemplate === 'smart_medication_picker') return 'I want to make sure nothing clashes. You can search or browse by category.'
  if (stage.stageTemplate === 'medication_allergies_picker') return "Even if it's minor — better safe than sorry!"
  if (stage.stageTemplate === 'last_dose' || stage.name === 'past_glp1') return 'This helps me personalise your starting dose safely.'
  if (stage.stageTemplate === 'review_submit' || stage.name === 'review_submit') return 'Your assessment is complete. A licensed clinician will review it shortly.'
  if (stage.stageTemplate === 'payment_method') return 'Your card will only be charged if your prescription is approved.'
  return ''
}

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const savedTheme = localStorage.getItem('ww-theme') as 'dark' | 'light' | null
    if (savedTheme) {
      setTheme(savedTheme)
    }
  }, [])

  useEffect(() => {
    if (!mounted) return
    const layout = document.querySelector('.ww-layout')
    if (layout) {
      layout.setAttribute('data-theme', theme)
    }
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('ww-theme', theme)
  }, [theme, mounted])

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  return { theme, toggleTheme, isDark: theme === 'dark', mounted }
}

function stringifyAnswer(value: AnswerValue): string {
  if (value === undefined || value === null) return 'Completed'
  if (Array.isArray(value)) return value.join(', ') || 'Completed'
  if (typeof value === 'string') return value || 'Completed'
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value instanceof Date) return value.toLocaleDateString('en-US')
  if (typeof value === 'object') {
    const pieces = Object.values(value as Record<string, unknown>)
      .filter((v) => ['string', 'number', 'boolean'].includes(typeof v))
      .map((v) => String(v))
      .filter(Boolean)
    return pieces.length ? pieces.slice(0, 2).join(' · ') : 'Completed'
  }
  return 'Completed'
}

function formatYmdToMdy(dateText?: string): string {
  if (!dateText) return ''
  const parts = dateText.split('-')
  if (parts.length !== 3) return dateText
  return `${parts[1]}/${parts[2]}/${parts[0]}`
}

function getOptionLabelFromStage(stage: WorkflowStage, optionId?: string): string {
  if (!optionId) return ''
  const optionsVar = stage.templateVariables.find((variable) => variable.title === 'options')
  const options = Array.isArray(optionsVar?.value) ? (optionsVar?.value as Array<{ id?: string; label?: string }>) : []
  const matched = options.find((option) => option.id === optionId)
  return matched?.label || optionId
}

function getCollapsedHeading(stage: WorkflowStage): string {
  const headerVar = stage.templateVariables.find((variable) => variable.title === 'header')
  if (typeof headerVar?.value === 'string' && headerVar.value.trim()) return headerVar.value

  if (stage.stageTemplate === 'basics') return "Great! Let's start here"
  if (stage.stageTemplate === 'bmi_calculator') return 'Now, a little more about you'
  if (stage.stageTemplate === 'goals') return 'So, now tell me what brought you here today?'
  if (stage.stageTemplate === 'therapy_preview') return "Now, I'll recap what you shared with me."
  if (stage.stageTemplate === 'smart_medication_picker') return 'Are you taking any other medications right now?'
  if (stage.stageTemplate === 'medication_allergies_picker') return 'Any allergies to medications I should know about?'
  if (stage.stageTemplate === 'safety_screening') return 'I need to ask you a few important health questions'
  if (stage.stageTemplate === 'past_glp1' || stage.stageTemplate === 'last_dose' || stage.name === 'past_glp1') return "Tell me about your GLP-1 history"
  if (stage.stageTemplate === 'id_upload') return 'Can I see a quick photo of your ID?'
  if (stage.stageTemplate === 'body_photo') return 'One more thing — can you share a full body photo?'
  if (stage.stageTemplate === 'review_submit' || stage.name === 'review_submit') return "You're all set — let's get you started"
  if (stage.stageTemplate === 'payment_method') return 'One last step — securely add your payment method'

  return stage.name.replace(/_/g, ' ')
}

function getCollapsedSummary(stage: WorkflowStage, answers: AnswerMap): string {
  if (stage.stageTemplate === 'goals') {
    const goalLabels = Array.isArray(answers.goals_selected_labels)
      ? (answers.goals_selected_labels as string[]).filter(Boolean)
      : []
    if (goalLabels.length) return goalLabels.join(', ')

    const fallbackGoals = Array.isArray(answers.goals_selected)
      ? (answers.goals_selected as string[]).filter(Boolean)
      : []
    if (fallbackGoals.length) {
      return fallbackGoals
        .map((goal) => goal.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()))
        .join(', ')
    }
  }

  if (stage.stageTemplate === 'basics') {
    const dob = formatYmdToMdy(answers.date_of_birth as string | undefined)
    const sex = (answers.sex_at_birth as string | undefined) === 'male' ? 'Male' : (answers.sex_at_birth as string | undefined) === 'female' ? 'Female' : ''
    const text = [dob, sex].filter(Boolean).join(' · ')
    return text || 'Completed'
  }
  if (stage.stageTemplate === 'bmi_calculator') {
    const feet = answers.height_feet as number | undefined
    const inches = answers.height_inches as number | undefined
    const weight = answers.weight_lbs as number | undefined
    const parts: string[] = []
    if (feet !== undefined) parts.push(`${feet}'${inches || 0}"`)
    if (weight !== undefined) parts.push(`${weight} lbs`)
    return parts.join(' · ') || 'Completed'
  }
  if (stage.stageTemplate === 'which_medications') {
    const field = stage.answerMapping?.field
    const selected = field && Array.isArray(answers[field]) ? (answers[field] as string[]) : []
    if (!selected.length) return 'Completed'
    return selected.map((optionId) => getOptionLabelFromStage(stage, optionId)).join(', ')
  }
  const field = stage.answerMapping?.field
  if (stage.stageTemplate === 'radio_list' && field) {
    const selectedId = answers[field] as string | undefined
    return getOptionLabelFromStage(stage, selectedId) || 'Completed'
  }
  return stringifyAnswer(field ? answers[field] : undefined)
}

const iconBtn =
  'flex items-center justify-center w-9 h-9 rounded-full bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] text-[var(--ww-text-2)] cursor-pointer transition-all duration-200 glass hover:bg-[var(--ww-surface-2)] hover:text-[var(--ww-text)] hover:border-[var(--ww-border-hover)]'

const backBtn =
  'flex items-center justify-center w-8 h-8 rounded-full bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] text-[var(--ww-text-2)] cursor-pointer transition-all duration-200 glass hover:bg-[var(--ww-surface-2)] hover:text-[var(--ww-text)] hover:border-[var(--ww-border-hover)] shrink-0 disabled:opacity-15 disabled:cursor-default'

function ActiveStageBlock({
  currentStage,
  phaseLabel,
  isTransitioning,
  onTypingComplete,
  children,
}: {
  currentStage: WorkflowStage | null
  phaseLabel: string
  isTransitioning: boolean
  onTypingComplete?: () => void
  children: React.ReactNode
}) {
  const [typingDone, setTypingDone] = useState(false)
  const [entered, setEntered] = useState(false)
  const isIntro = currentStage?.stageTemplate === 'intro'
  const title = useMemo(() => getStageHeading(currentStage), [currentStage])
  const subtitle = useMemo(() => getStageSubtitle(currentStage), [currentStage])

  useEffect(() => {
    setTypingDone(false)
    setEntered(false)
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [currentStage?.id])

  const handleTypingComplete = useCallback(() => {
    setTypingDone(true)
    onTypingComplete?.()
  }, [onTypingComplete])

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${isTransitioning ? 'step-exit' : ''} ${!isTransitioning && entered ? 'step-enter-active' : 'step-enter'}`}>
      {!isIntro && (
        <div className="flex gap-2.5 pt-5 pb-5 items-start question-header">
          <NimyAvatar className="mt-0.5" />
          <div className="flex-1 min-w-0">
            {phaseLabel && (
              <span className="block text-[9px] font-semibold tracking-[0.12em] uppercase text-[var(--ww-accent)] mb-0.5 opacity-70">
                {phaseLabel}
              </span>
            )}
            <h2 className="font-secondary text-[clamp(24px,4vw,30px)] font-normal leading-tight text-[var(--ww-text)] m-0">
              <TypingText text={title} onComplete={handleTypingComplete} />
            </h2>
            {subtitle && typingDone && (
              <div className="text-sm leading-relaxed text-[var(--ww-text-2)] mt-1.5 animate-fade-up">
                <p className="m-0">{subtitle}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`flex-1 flex flex-col ${!isIntro ? 'pl-[42px]' : ''} step-body-hidden ${typingDone || isIntro ? 'step-body-visible' : ''}`}>
        {children}
      </div>
    </div>
  )
}

function Glp1QuizContent({
  initialAnswers,
  initialStageId,
  initialCompletedAtByStage,
  progressStorageKey,
  onSubmit,
}: Glp1QuizPageProps) {
  const { currentVibe } = useVibe()
  const { theme, toggleTheme } = useTheme()
  const activeStageRef = useRef<HTMLDivElement>(null)
  const [showValidationMessage, setShowValidationMessage] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const {
    currentStage,
    visibleStages,
    currentStageIndex,
    totalStages,
    progress,
    currentPhase,
    answers,
    completedAtByStage,
    guidanceData,
    isLoading,
    isFirstStage,
    isLastStage,
    getAnswer,
    setAnswer,
    setMultipleAnswers,
    onNext,
    onBack,
    goToStage,
    validateCurrentStage,
    getSubmissionPayload,
  } = useClinicWorkflow(initialAnswers as AnswerMap, initialStageId, initialCompletedAtByStage)

  const isValid = validateCurrentStage()

  const handleNextStep = async () => {
    await onNext()
  }

  const handleSubmitIntake = async () => {
    const payload = getSubmissionPayload()
    // If payment_method_data was already set via setAnswer, it will be in the payload
    // from the hook. If not yet propagated, we read directly from the latest answers ref.
    await onSubmit(payload)
    setIsSubmitted(true)
  }

  const handlePrevStep = () => {
    if (isFirstStage) return
    onBack()
  }

  const scrollToShowQuestion = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const scrollContainer = activeStageRef.current?.closest('.overflow-y-auto')
    if (!scrollContainer) return
    const questionHeader = activeStageRef.current?.querySelector('.question-header')
    if (questionHeader) {
      const progressHeader = document.querySelector('header.sticky')
      const progressHeaderHeight = progressHeader ? progressHeader.getBoundingClientRect().height : 0
      const headerTop = questionHeader.getBoundingClientRect().top
      const containerTop = scrollContainer.getBoundingClientRect().top
      const scrollOffset = scrollContainer.scrollTop + (headerTop - containerTop) - progressHeaderHeight
      scrollContainer.scrollTo({ top: scrollOffset, behavior })
    } else {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior })
    }
  }, [])

  const handleTypingComplete = useCallback(() => {
    setTimeout(() => scrollToShowQuestion(), 100)
    setTimeout(() => scrollToShowQuestion(), 700)
  }, [scrollToShowQuestion])

  useEffect(() => {
    if (!activeStageRef.current) return
    const t1 = setTimeout(() => scrollToShowQuestion(), 100)
    const t2 = setTimeout(() => scrollToShowQuestion(), 1500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [currentStageIndex, scrollToShowQuestion])

  useEffect(() => {
    if (!progressStorageKey) return
    try {
      localStorage.setItem(
        progressStorageKey,
        JSON.stringify({
          version: 1,
          savedAt: new Date().toISOString(),
          answers,
          stageId: currentStage?.id || null,
          completedAtByStage,
        })
      )
    } catch {
      // Ignore storage errors
    }
  }, [answers, completedAtByStage, currentStage?.id, progressStorageKey])

  const completedStageEntries = useMemo(
    () =>
      visibleStages
        .slice(0, currentStageIndex)
        .map((stage, stageIndex) => ({ stage, stageIndex }))
        .filter(({ stage }) => stage.stageTemplate !== 'intro'),
    [visibleStages, currentStageIndex]
  )

  const stageHandlesNavigation = [
    'intro',
    'text_area',
    'basics',
    'bmi_calculator',
    'bmi_education',
    'comorbidity',
    'dosage',
    'experience',
    'goals',
    'last_dose',
    'life_context',
    'which_medications',
    'therapy_preview',
    'email_capture',
    'safety_screening',
    'past_glp1',
    'smart_medication_picker',
    'medication_allergies_picker',
    'medicine_selection',
    'id_upload',
    'body_photo',
    'review_submit',
    'payment_method',
  ].includes(currentStage?.stageTemplate || '')
  const hideBottomContinue = stageHandlesNavigation || currentStage?.id === 'ww-glp1-experience'

  if (isSubmitted) {
    return (
      <div className={`weightwise-theme vibe-${currentVibe}`} data-vibe={currentVibe} data-theme={theme}>
        <div className="ww-layout">
          <div className="ww-scroll-container scrollbar-hide">
            <div className="flex min-h-screen items-center justify-center px-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] p-8 text-center shadow-lg glass"
              >
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--ww-accent-dim)]">
                  <CheckCircle2 className="h-10 w-10 text-[var(--ww-accent)]" />
                </div>
                <h1 className="mb-4 font-secondary text-3xl font-bold text-[var(--ww-text)]">Thank You!</h1>
                <p className="mb-6 text-lg text-[var(--ww-text-2)]">
                  Your weight loss assessment has been submitted successfully.
                </p>
                <div className="mb-8 rounded-xl border border-[var(--ww-accent)]/30 bg-[var(--ww-accent-dim)] p-6">
                  <h3 className="mb-3 font-semibold text-[var(--ww-accent)]">Next Steps</h3>
                  <ul className="space-y-2 text-left text-sm text-[var(--ww-text)]">
                    <li>&bull; A licensed clinician will review your assessment</li>
                    <li>&bull; You&apos;ll receive an email with next steps within 24 hours</li>
                    <li>&bull; If approved, your prescription will be sent to the pharmacy</li>
                    <li>&bull; Our team may contact you if additional information is needed</li>
                  </ul>
                </div>
                <p className="text-sm text-[var(--ww-text-3)]">You can close this window now.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`weightwise-theme vibe-${currentVibe}`} data-vibe={currentVibe} data-theme={theme}>
      <div className="ww-layout">
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
            {/* Sticky header */}
            <header className="sticky top-0 z-50 w-full backdrop-blur-xl shrink-0 ww-sticky-header" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
              <div className="max-w-[520px] w-full mx-auto px-5">
                <div className="flex items-center justify-between md:my-4 my-2 md:mb-6 mb-4 pt-3.5 py-1">
                  <div className="ww-logo">
                    <img src="/img/nim-logo-black.svg" alt="Nimbus Healthcare" width={140} height={40} className="w-[140px] h-auto" />
                  </div>
                  <div className="flex items-center gap-2">
                    <button className={iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
                      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 pb-3.5">
                  <button
                    className={backBtn}
                    onClick={handlePrevStep}
                    disabled={isFirstStage}
                    aria-label="Go back"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M5 12L12 19M5 12L12 5" />
                    </svg>
                  </button>
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ww-accent)] whitespace-nowrap">
                        {currentPhase?.name || 'Getting Started'}
                      </span>
                      <span className="text-[11px] font-medium text-[var(--ww-text-3)]">
                        {currentStageIndex + 1} of {totalStages}
                      </span>
                    </div>
                    <div className="h-[3px] bg-[var(--ww-surface-2)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--ww-accent)] rounded-full transition-[width] duration-[600ms] ease-out"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Main content area */}
            <div className="ww-page-responsive flex-1 flex flex-col max-w-[520px] w-full mx-auto px-5">
              <main className="flex-1 flex flex-col">
                <div className="flex-1 flex flex-col" ref={activeStageRef}>
                  {/* Chat history (completed stages) */}
                  {completedStageEntries.length > 0 && (
                    <div className="flex flex-col gap-0.5 pb-3 border-b border-[var(--ww-border)] mb-2">
                      {completedStageEntries.map(({ stage, stageIndex }, i) => (
                        <div key={stage.id} className="py-2.5 animate-msg-in" style={{ animationDelay: `${i * 30}ms` }}>
                          <div className="flex gap-2.5 items-start mb-1.5">
                            <NimyAvatar className="mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <span className="block text-[9px] font-semibold tracking-[0.12em] uppercase text-[var(--ww-accent)] mb-0.5 opacity-70">
                                {PHASE_LABELS[stage.phase || 1] || 'INTAKE'}
                              </span>
                              <p className="text-sm leading-snug text-[var(--ww-text-2)] m-0">{getCollapsedHeading(stage)}</p>
                            </div>
                          </div>

                          <div className="flex justify-end pl-[42px]">
                            <div
                              className="group inline-flex items-center gap-2 max-w-[80%] px-3.5 py-2 bg-[var(--ww-reply-bg)] border border-[var(--ww-reply-border)] rounded-2xl rounded-br-lg transition-all duration-200 glass cursor-pointer hover:bg-[var(--ww-surface-2)] hover:border-[var(--ww-border-hover)] hover:-translate-y-px"
                              onClick={() => goToStage(stageIndex)}
                            >
                              <span className="text-[13.5px] font-medium text-[var(--ww-text)] leading-snug overflow-hidden text-ellipsis whitespace-nowrap">
                                {getCollapsedSummary(stage, answers)}
                              </span>
                              <span className="shrink-0 text-[var(--ww-accent)] opacity-70 group-hover:opacity-100 transition-all duration-200">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Current active stage */}
                  <ActiveStageBlock
                    key={currentStage?.id}
                    currentStage={currentStage}
                    phaseLabel={PHASE_LABELS[currentStage?.phase || 1] || 'INTAKE'}
                    isTransitioning={false}
                    onTypingComplete={handleTypingComplete}
                  >
                    <StageRenderer
                      stage={currentStage}
                      answers={answers}
                      getAnswer={getAnswer}
                      setAnswer={setAnswer}
                      setMultipleAnswers={setMultipleAnswers}
                      guidanceData={guidanceData}
                      onContinue={handleNextStep}
                      onSubmit={handleSubmitIntake}
                      currentPhase={currentPhase}
                    />
                  </ActiveStageBlock>

                  <ValidationMessage show={showValidationMessage} message="Please complete this step to continue." />

                  {!hideBottomContinue && (
                    <div className="mt-auto pt-6 pb-2 pl-[42px]">
                      <div className="-ml-[42px] w-[calc(100%+42px)]">
                        <button
                          onClick={() => {
                            if (isValid) {
                              void handleNextStep()
                            } else {
                              setShowValidationMessage(true)
                              setTimeout(() => setShowValidationMessage(false), 3000)
                            }
                          }}
                          disabled={!isValid || isLoading}
                          className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full bg-[var(--ww-btn-bg)] px-6 py-4 text-[15px] font-semibold text-[var(--ww-btn-text)] border-none shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-[0_8px_24px_rgba(0,0,0,0.15)] active:not-disabled:translate-y-0 disabled:opacity-25 disabled:cursor-default transition-all duration-300"
                        >
                          {isLoading ? (
                            <>
                              <Loader2Icon className="h-5 w-5 animate-spin" />
                              Processing...
                            </>
                          ) : isLastStage ? (
                            'Submit'
                          ) : (
                            'Continue'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </main>

              {currentStage?.stageTemplate !== 'intro' && (
                <footer className="flex items-center justify-center gap-4 py-4 pb-6 shrink-0">
                  <div className="flex items-center gap-1.5 text-[10px] text-[var(--ww-text-3)] uppercase tracking-[0.06em]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--ww-text-3)]">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    <span>HIPAA Compliant</span>
                  </div>
                  <span className="w-px h-3 bg-[var(--ww-border)]" />
                  <div className="flex items-center gap-1.5 text-[10px] text-[var(--ww-text-3)] uppercase tracking-[0.06em]">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--ww-text-3)]">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>Secure &amp; Confidential</span>
                  </div>
                </footer>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Glp1QuizPage(props: Glp1QuizPageProps) {
  return (
    <VibeProvider>
      <Glp1QuizContent {...props} />
    </VibeProvider>
  )
}
